<template>
  <div v-loading="loading" class="exchange-detail">
    <template v-if="article">
      <!-- 作者列 -->
      <div class="detail-header">
        <div class="author-block">
          <img
            class="avatar"
            :src="article.authorAvatar || defaultAvatar"
            @error="onAvatarError"
            alt="avatar"
          />
          <div class="author-meta">
            <div class="author-name">
              {{ article.authorName || '匿名' }}
              <el-tag
                v-if="article.tagName"
                size="mini"
                effect="dark"
                :style="tagStyle(article.tagColor)"
                class="tag-chip"
              >
                {{ article.tagName }}
              </el-tag>
              <el-tag v-if="isDraft" size="mini" type="info" class="tag-chip">
                尚未發佈
              </el-tag>
            </div>
            <div class="author-date">{{ formatDate(article.buildDate) }}</div>
          </div>
        </div>

        <div class="header-actions">
          <el-button
            v-if="article.canEdit"
            size="mini"
            type="primary"
            icon="el-icon-edit"
            @click="$emit('edit', article)"
          >
            編輯
          </el-button>
          <el-button
            v-if="article.canEdit"
            size="mini"
            type="danger"
            icon="el-icon-delete"
            @click="handleDelete"
          >
            刪除
          </el-button>
          <el-button size="mini" icon="el-icon-close" @click="$emit('close')">關閉</el-button>
        </div>
      </div>

      <!-- 標題 -->
      <h1 class="detail-title">{{ article.title }}</h1>

      <!-- 封面 -->
      <div v-if="coverUrl" class="detail-cover">
        <el-image :src="coverUrl" fit="contain" :preview-src-list="[coverUrl]" />
      </div>

      <!-- 內文 -->
      <div class="detail-content" v-html="article.content"></div>

      <!-- 留言區 -->
      <div class="comment-section">
        <div class="comment-title">
          <i class="el-icon-chat-dot-round"></i>
          留言 ({{ comments.length }})
        </div>

        <!-- 我要留言 -->
        <div class="comment-input">
          <el-input
            v-model="newComment"
            type="textarea"
            :rows="3"
            maxlength="1000"
            show-word-limit
            placeholder="我要留言..."
          />
          <div class="comment-input-actions">
            <el-button
              size="mini"
              type="primary"
              :loading="commentSubmitting"
              @click="submitComment"
            >
              送出留言
            </el-button>
          </div>
        </div>

        <!-- 留言列表 -->
        <div v-if="comments.length === 0" class="comment-empty">目前尚無留言，快來搶頭香吧。</div>

        <div v-for="c in comments" :key="c.id" class="comment-item">
          <img
            class="avatar avatar-sm"
            :src="c.commenterAvatar || defaultAvatar"
            @error="onAvatarError"
            alt="avatar"
          />
          <div class="comment-body">
            <div class="comment-meta">
              <span class="comment-name">{{ c.commenterName || '匿名' }}</span>
              <span class="comment-date">{{ formatDate(c.buildDate) }}</span>
              <el-button
                v-if="c.canDelete"
                class="comment-del"
                type="text"
                size="mini"
                @click="handleDeleteComment(c)"
              >
                刪除
              </el-button>
            </div>
            <div class="comment-text">{{ c.content }}</div>
          </div>
        </div>
      </div>
    </template>

    <el-empty v-else-if="!loading" description="找不到文章或已被移除"></el-empty>
  </div>
</template>

<script>
import { Message } from 'element-ui'
import { getArticle, saveComment, deleteComment, deleteArticle } from '@/api/agencyExchange'
import { encodeContentBase64 } from '@/utils/funcs'
import defaultAvatar from '@/assets/images/default_agency_avatar.png'

export default {
  name: 'AgencyExchangeDetail',
  props: {
    id: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      defaultAvatar,
      loading: false,
      article: null,
      comments: [],
      newComment: '',
      commentSubmitting: false,
    }
  },
  computed: {
    isDraft() {
      return this.article && (this.article.isPublic === false || this.article.isPublic === 0)
    },
    coverUrl() {
      const a = this.article
      if (a && a.fileInfo && a.fileInfo.length > 0) {
        const files = a.fileInfo[0].files
        if (files && files.length > 0) {
          return files[0].url
        }
      }
      return ''
    },
  },
  watch: {
    id: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadArticle()
        }
      },
    },
  },
  methods: {
    tagStyle(color) {
      const c = color || '#909399'
      return { backgroundColor: c, borderColor: c, color: '#fff' }
    },
    formatDate(val) {
      if (!val) return ''
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`
    },
    onAvatarError(e) {
      // 每個 img 只 fallback 一次，杜絕無限迴圈
      if (!e.target.dataset.fallbackApplied) {
        e.target.dataset.fallbackApplied = '1'
        e.target.src = this.defaultAvatar
      }
    },
    async loadArticle() {
      this.loading = true
      try {
        const resp = await getArticle(this.id)
        if (resp && resp.success) {
          this.article = resp.data || null
          this.comments = (this.article && this.article.comments) || []
        } else {
          this.article = null
          this.comments = []
        }
      } catch (e) {
        this.article = null
        this.comments = []
      } finally {
        this.loading = false
      }
    },
    async submitComment() {
      const content = (this.newComment || '').trim()
      if (!content) {
        Message.error('請輸入留言內容')
        return
      }
      this.commentSubmitting = true
      try {
        // 留言內容 base64 編碼，避免 AWS WAF 誤擋 403；後端 SaveComment 會先 Base64ContentCodec.Decode 還原
        const resp = await saveComment({ articleId: this.id, content: encodeContentBase64(content) })
        if (resp && resp.success) {
          this.newComment = ''
          Message({ message: '留言成功', type: 'success', duration: 3000 })
          await this.loadArticle()
          this.$emit('changed')
        }
      } finally {
        this.commentSubmitting = false
      }
    },
    handleDeleteComment(c) {
      this.$confirm('確定刪除這則留言?', '警告', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          const resp = await deleteComment({ id: c.id })
          if (resp && resp.success) {
            Message({ message: '已刪除留言', type: 'success', duration: 3000 })
            await this.loadArticle()
            this.$emit('changed')
          }
        })
        .catch(() => {})
    },
    handleDelete() {
      this.$confirm('確定刪除這篇文章?', '警告', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          const resp = await deleteArticle({ id: this.id })
          if (resp && resp.success) {
            Message({ message: '文章已刪除', type: 'success', duration: 3000 })
            this.$emit('deleted')
          }
        })
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.exchange-detail {
  padding: 10px 24px 24px;
  min-height: 300px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 14px;
  border-bottom: 1px solid #ebeef5;
}

.author-block {
  display: flex;
  align-items: center;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  background: #f2f3f5;
  margin-right: 12px;
  flex-shrink: 0;
}

.avatar-sm {
  width: 40px;
  height: 40px;
}

.author-meta .author-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-chip {
  margin-left: 4px;
}

.author-meta .author-date {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.detail-title {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
  margin: 20px 0 16px;
  word-break: break-word;
}

.detail-cover {
  margin-bottom: 20px;
  text-align: center;
}

.detail-cover .el-image {
  max-width: 100%;
  max-height: 420px;
}

.detail-content {
  font-size: 15px;
  line-height: 1.8;
  color: #303133;
  word-break: break-word;
}

.detail-content ::v-deep img {
  max-width: 100%;
}

.comment-section {
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.comment-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
}

.comment-input {
  margin-bottom: 24px;
}

.comment-input-actions {
  margin-top: 8px;
  text-align: right;
}

.comment-empty {
  color: #909399;
  font-size: 14px;
  padding: 16px 0;
}

.comment-item {
  display: flex;
  padding: 14px 0;
  border-bottom: 1px solid #f2f3f5;
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.comment-name {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}

.comment-date {
  font-size: 12px;
  color: #909399;
}

.comment-del {
  color: #f56c6c;
  padding: 0;
}

.comment-text {
  margin-top: 6px;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  word-break: break-word;
  white-space: pre-wrap;
}
</style>
